import {
  ScreenCardInView,
  ScreenFilterChip
} from 'containers/Intelligence/filters/screenFilterSignature';

import { TOrbit } from './i18n/TModules';
import { getOrbitScreenLabelKey } from './screenLabels';
import { OrbitEntryContext, OrbitTurn } from './types';

type Translate = (key: string, values?: Record<string, unknown>) => string;

/**
 * Human name for an entry context. Two customer contexts on the same screen render the same label,
 * so the customer is appended whenever there is one — otherwise the conflict card would compare a
 * value against itself. The name wins over the id when the route has loaded one: v3 writes the
 * handoff line as "Opened Customer 360 — Priya Sharma" (`5017:8284`), not as a record number.
 */
export const describeOrbitContext = (t: Translate, context: OrbitEntryContext | null): string => {
  const labelKey = context === null ? undefined : getOrbitScreenLabelKey(context.screen);

  if (labelKey === undefined || context === null) {
    return t(`${TOrbit}.conflict.unknown`);
  }

  const screen = t(labelKey);

  if (context.customer_name !== undefined) {
    return t(`${TOrbit}.conflict.customerName`, { screen, name: context.customer_name });
  }

  return context.customer_id === undefined
    ? screen
    : t(`${TOrbit}.conflict.customer`, { screen, id: context.customer_id });
};

/**
 * The screen's own label, with no customer appended: the handoff card's step line names the screen
 * the authorization was refreshed against (`Fresh Customer 360 authorization performed`), not the
 * record. `undefined` when the route resolves to a screen Orbit has no label for.
 */
export const describeOrbitScreen = (
  t: Translate,
  context: OrbitEntryContext | null
): string | undefined => {
  const labelKey = context === null ? undefined : getOrbitScreenLabelKey(context.screen);

  return labelKey === undefined ? undefined : t(labelKey);
};

/**
 * A long question would push the compare row past the card. 60 characters is a product cap, not a
 * measurement: nothing in the frames bounds this value, and the composer accepts 500.
 */
const SUBJECT_MAX = 60;

/** Cut to 60 characters plus `…`: the same cap the server puts on a chat's default title. */
export const clampOrbitLabel = (value: string): string =>
  value.length <= SUBJECT_MAX ? value : `${value.slice(0, SUBJECT_MAX).trimEnd()}…`;

/**
 * What the running investigation is ABOUT, from its opening turn. `undefined` when the session has
 * no loaded turn, or when the turn carries neither a measured metric nor a question.
 *
 * Nothing on the wire carries an investigation subject of the shape v3 draws — "High-value
 * customers · Last 30 days" (`5032:8002`) is the EMEA demo's own dashboard filters, and neither the
 * PLA-8839 context object nor the turn payload has a field for a segment or a date range. The
 * closest truthful values are the backend's own name for what it measured and, failing that, the
 * question the investigation opened with.
 *
 * Shared so the conflict card's "Investigating" row and the launcher's label card cannot drift.
 */
export const orbitSessionSubject = (turns: OrbitTurn[]): string | undefined => {
  const opening = turns.length > 0 ? turns[turns.length - 1] : undefined;

  if (opening === undefined) {
    return undefined;
  }

  // The first metric only. A turn that answered across several has no one subject, and joining the
  // names would read as a compound the backend never asserted.
  const metricName = opening.metric_results[0]?.metric_name;
  const subject = metricName !== undefined && metricName !== '' ? metricName : opening.question;

  return subject === '' ? undefined : clampOrbitLabel(subject);
};

/**
 * The conflict card's "Investigating" row. The screen is appended to the subject because the card
 * exists to report a screen CHANGE: without it the row states a subject and never says what moved.
 */
export const describeOrbitSubject = (
  t: Translate,
  turns: OrbitTurn[],
  sessionContext: OrbitEntryContext | null
): string => {
  const screen = describeOrbitContext(t, sessionContext);
  const subject = orbitSessionSubject(turns);

  return subject === undefined ? screen : t(`${TOrbit}.conflict.subject`, { subject, screen });
};

export default describeOrbitContext;

/**
 * The text one filter chip draws. The date-range chip keeps the frame's own `📅 Period:` wording
 * (`5032:12031`, `5032:8070`) rather than the filter bar's "Date Range" heading; every other chip
 * uses the screen's own category name, so the panel and the page read as one. PLA-8844.
 */
export const describeOrbitFilterChip = (t: Translate, chip: ScreenFilterChip): string =>
  chip.id === 'period'
    ? t(`${TOrbit}.context.period`, { period: chip.value })
    : t(`${TOrbit}.context.chip`, { label: chip.label, value: chip.value });

/**
 * The text the card chip draws: the card's own heading, as the screen prints it. The catalogue id is
 * deliberately not shown - it is what goes on the wire (`context.metric_id`), not what names a card
 * to a reader. PLA-8844.
 */
export const describeOrbitCardChip = (t: Translate, card: ScreenCardInView): string =>
  t(`${TOrbit}.context.card`, { card: card.title });

/**
 * How many card chips the context row draws before the rest collapse into a count.
 *
 * The frame's chip row (`5032:12025`) is a fixed 436.5 x 64 box - two 28px rows at an 8px gap - but
 * it draws three chips and no card chip at all, so it constrains no card-chip count: four titles at
 * the drawn 10.5px would overflow those two rows whatever cap is chosen, and `OrbitChipRow` wraps
 * freely rather than clipping. 4 is therefore a product cap on how much of the panel the context
 * block may take, the same kind of cap `SUBJECT_MAX` above is. PLA-8844.
 */
export const ORBIT_CARD_CHIP_LIMIT = 4;

/** The chip standing in for the cards past the cap. */
export const describeOrbitMoreCards = (t: Translate, count: number): string =>
  t(`${TOrbit}.context.cardsMore`, { count });
